import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@apollo/client'

import { Button, CardBox, Fields, Row, Stack, Text, useAlert } from '../../components'
import { validations } from '../../components/form'
import { mutations, queries } from '../../graphql'

export function CustodianSettings() {
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const { data: meData } = useQuery(queries.user.me)
  const me = meData?.me

  const { data: settingsData, loading: settingsLoading } = useQuery(queries.user.myAdvisorCustodianSettings)
  const existingSettings = settingsData?.myAdvisorCustodianSettings

  // Fetch suggested custodian name from donor requests (only needed when no settings exist yet)
  const { data: suggestedData } = useQuery(queries.user.suggestedCustodianName, {
    skip: settingsLoading || !!existingSettings,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      custodianName: '',
      advisorName: '',
      advisorEmail: '',
      advisorPhone: '',
      advisorDealerRepCode: '',
      address: {
        lineOne: '',
        city: '',
        province: '',
        postalCode: '',
      },
    },
  })

  // Populate form with existing settings, or pre-fill from profile
  useEffect(() => {
    if (settingsLoading) return

    if (existingSettings) {
      reset({
        custodianName: existingSettings.custodianName || '',
        advisorName: existingSettings.advisorName || '',
        advisorEmail: existingSettings.advisorEmail || '',
        advisorPhone: existingSettings.advisorPhone || '',
        advisorDealerRepCode: existingSettings.advisorDealerRepCode || '',
        address: {
          lineOne: existingSettings.address?.lineOne || '',
          city: existingSettings.address?.city || '',
          province: existingSettings.address?.province || '',
          postalCode: existingSettings.address?.postalCode || '',
        },
      })
    } else if (me) {
      const advisorName = me.name || `${me.profile?.firstName || ''} ${me.profile?.lastName || ''}`.trim()
      reset({
        custodianName: suggestedData?.suggestedCustodianName || '',
        advisorName: advisorName || '',
        advisorEmail: me.email || '',
        advisorPhone: me.profile?.phone || '',
        advisorDealerRepCode: '',
        address: {
          lineOne: '',
          city: '',
          province: '',
          postalCode: '',
        },
      })
    }
  }, [settingsLoading, existingSettings, me, suggestedData, reset])

  const [updateSettings] = useMutation(mutations.user.updateAdvisorCustodianSettings, {
    refetchQueries: [{ query: queries.user.myAdvisorCustodianSettings }],
  })

  const onSubmit = async (data) => {
    try {
      await updateSettings({ variables: { data } })
      setAlert({ message: 'Custodian settings saved', severity: 'success' })
    } catch (error) {
      setAlert({
        message: error.message || 'Failed to save settings',
        severity: 'error',
      })
    }
  }

  if (settingsLoading) {
    return (
      <CardBox>
        <Text.Body>Loading...</Text.Body>
      </CardBox>
    )
  }

  return (
    <CardBox>
      <Text.H4>Custodian Settings</Text.H4>
      <br />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack>
          <Text.Body>
            This information will be used to pre-fill securities contribution forms when acting on behalf of your
            clients.
          </Text.Body>

          <Text.H6>Securities Firm Information</Text.H6>

          <Fields.Text
            inputProps={{ 'data-testid': 'custodianName' }}
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Name of Securities Firm *"
            error={!!errors?.custodianName}
            helperText={errors?.custodianName?.message}
            {...register('custodianName', {
              required: 'This field is required',
              validate: {
                noSpecialCharacters: (value) => /^[a-zA-Z0-9\s]*$/.test(value) || 'No special characters allowed',
              },
            })}
          />

          <Text.H6>Advisor Information</Text.H6>

          <Row>
            <Fields.Text
              inputProps={{ 'data-testid': 'advisorName' }}
              type="text"
              label="Contact Name (Advisor) *"
              error={!!errors?.advisorName}
              helperText={errors?.advisorName?.message}
              {...register('advisorName', { ...validations.required })}
            />
            <Fields.Text
              inputProps={{ 'data-testid': 'advisorDealerRepCode' }}
              type="text"
              label="Advisor Dealer Rep Code"
              error={!!errors?.advisorDealerRepCode}
              helperText={errors?.advisorDealerRepCode?.message}
              {...register('advisorDealerRepCode')}
            />
          </Row>

          <Row>
            <Fields.Text
              inputProps={{ 'data-testid': 'advisorEmail' }}
              type="text"
              label="Advisor Email *"
              error={!!errors?.advisorEmail}
              helperText={errors?.advisorEmail?.message}
              {...register('advisorEmail', { ...validations.required, ...validations.email })}
            />
            <Fields.Text
              inputProps={{ 'data-testid': 'advisorPhone' }}
              type="text"
              label="Advisor Phone Number *"
              error={!!errors?.advisorPhone}
              helperText={errors?.advisorPhone?.message}
              {...register('advisorPhone', { ...validations.required, ...validations.phoneNumber })}
            />
          </Row>

          <Text.H6>Custodian Address</Text.H6>

          <Fields.Text
            inputProps={{ 'data-testid': 'address.lineOne' }}
            type="text"
            label="Securities Dealer/Custodian Address *"
            error={!!errors?.address?.lineOne}
            helperText={errors?.address?.lineOne?.message}
            {...register('address.lineOne', { ...validations.required })}
          />

          <Row>
            <Fields.Text
              inputProps={{ 'data-testid': 'address.city' }}
              type="text"
              label="City *"
              error={!!errors?.address?.city}
              helperText={errors?.address?.city?.message}
              {...register('address.city', { ...validations.required })}
            />
            <Fields.Text
              inputProps={{ 'data-testid': 'address.province' }}
              type="text"
              label="Province/State *"
              error={!!errors?.address?.province}
              helperText={errors?.address?.province?.message}
              {...register('address.province', { ...validations.required })}
            />
            <Fields.Text
              inputProps={{ 'data-testid': 'address.postalCode' }}
              type="text"
              label="Postal Code/ZIP *"
              error={!!errors?.address?.postalCode}
              helperText={errors?.address?.postalCode?.message}
              {...register('address.postalCode', { ...validations.required })}
            />
          </Row>

          <Alert {...alertProps} />

          <Row sx={{ marginTop: '2rem!important' }} alignSelf="flex-end">
            <Button type="submit" disabled={isSubmitting} data-testid="save-settings-button">
              {isSubmitting ? 'Saving...' : 'Save Settings'}
            </Button>
          </Row>
        </Stack>
      </form>
    </CardBox>
  )
}
